import { QrCodeIcon } from "@phosphor-icons/react/dist/csr/QrCode";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { CustomerWithReferralsAndPayouts } from "@/customer/customer";
import { SIGNUP_SOURCE_CLASS_NAMES } from "@/referral-sources/constants";
import { SignupSource, UserSource } from "@/referral-sources/referral-source";
import { isTech } from "@/user/utils";

const Content = ({ signupSource }: { signupSource: SignupSource }) => {
  const navigate = useNavigate();
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { businessId } = useParams();

  const sourceName = signupSource.display_identification || "-";
  const isAttributed = sourceName !== "-";
  const isDiscarded = signupSource.discarded_at;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isDiscarded) return;

    if (signupSource.class_name === SIGNUP_SOURCE_CLASS_NAMES.user) {
      const user = signupSource as UserSource;
      const isTechUser = isTech(user.role);
      void navigate(
        `/business-settings/${businessId}/users/${isTechUser ? "technicians" : "web-app-users"}`,
      );
      updateSidebar({
        isOpen: true,
        mode: SIDEBAR_MODES.view,
        section: isTechUser ? "technicians" : "users",
        selectedItemId: user.id,
      });
    } else {
      void navigate(`/business-settings/${businessId}/referral-sources`);
      updateSidebar({
        isOpen: true,
        mode: SIDEBAR_MODES.view,
        section: "referral-sources",
        selectedItemId: signupSource.id,
      });
    }
  };

  return (
    <Stack direction="row" align="center" gap={2}>
      {isAttributed && (
        <QrCodeIcon className={cn("h-5 w-5", isDiscarded && "text-subtle")} />
      )}
      {isDiscarded ? (
        <Text tone="muted">{sourceName}</Text>
      ) : (
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 font-normal underline"
          onClick={handleClick}
        >
          {sourceName}
        </Button>
      )}
    </Stack>
  );
};

export const AttributedToCol =
  (): ColumnDef<CustomerWithReferralsAndPayouts> => ({
    header: "Attributed to",
    accessorFn: (row) => row.signup_source?.display_identification || "-",
    size: 160,
    minSize: 160,
    maxSize: 160,
    enableSorting: false,
    cell: ({ row }) => {
      if (!row.original.signup_source) {
        return "-";
      }
      return <Content signupSource={row.original.signup_source} />;
    },
  });
